import { useEffect, useCallback, useMemo } from 'react';
import { RtkMeeting } from '@cloudflare/realtimekit-react-ui';
import { useRealtimeKitClient, RealtimeKitProvider, useRealtimeKitMeeting } from '@cloudflare/realtimekit-react';
import { useVideoJoin } from './utils/useVideoJoin';
import JoinStatus from './JoinStatus';
import { getMeetingThemeDetails } from './utils/meetingTheme';
import { applyMeetingScopedPanelTheme } from './utils/applyMeetingScopedPanelTheme';
import { applyChatSelectorUiTokens } from './utils/applyChatPanelBrandOverride';
import {
  applyDialogChromeContrast,
  applyPrimaryMenuListTextContrast,
  applyStageAndSidebarLeafPatches,
} from './utils/applyRtkSidebarAndStageContrast';
import { injectRtkDebuggerLayoutOverrides } from './utils/injectRtkDebuggerLayoutOverrides';
import { applyScopedControlbarHoverBackground } from './utils/applyScopedControlbarHoverBackground';
import { applyRtkBrandTokensForFocusRing } from './utils/applyRtkBrandTokensForFocusRing';
import { resolveAccessibleForegroundForSurface } from './utils/wcagContrast';

function App() {
  const [rtkMeeting, initMeeting] = useRealtimeKitClient();
  const searchParams = new URL(window.location.href).searchParams;
  const meeting_id = searchParams.get('id') ?? searchParams.get('meetingId');

  const authToken = searchParams.get('authToken');
  const join = useVideoJoin(meeting_id, authToken, initMeeting, rtkMeeting);
  const response = join.metadata;
  useEffect(() => {
    if (response) {
      const button = document.querySelector('.end-meeting-all-btn');
      if (button) button.style.display = 'none';
    }
  }, [response]);

  if (!join.handoffReady) return <JoinStatus join={join} />;

  return (
    <RealtimeKitProvider value={rtkMeeting} fallback={<JoinStatus join={join} />}>
      <div className="background-blur">
        <RtkMeetingComponent response={response} />
      </div>
      <style>{`
        /* Avoid forcing host color: breaks light sidebar / icon contrast. */
        .background-blur {
          position: relative;
          height: 100vh;
          width: 100vw;
        }
        .background-blur::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          ${response?.data?.data?.dyteMeetingDetails?.background_image ? `
            background-image: url(${response.data.data.dyteMeetingDetails.background_image});
            background-size: cover;
            background-position: center;
            background-repeat: no-repeat;
            filter: blur(5px);
            width: 100vw;
            height: 100vh ;
          ` : `
            background-color: #f9ebeb;
          `}
          z-index: -1;
        }
      `}</style>
    </RealtimeKitProvider>
  );
}

function RtkMeetingComponent(response) {
  const { meeting } = useRealtimeKitMeeting();

  const buttonBackground = response?.response?.data?.data?.dyteThemeDataDetails?.buttonBackground;
  const buttonColor = response?.response?.data?.data?.dyteThemeDataDetails?.buttonColor;

  /** WCAG-safe icon/label color on `buttonBackground`; unchanged when API pair already passes (most themes). */
  const controlBarIconColor = useMemo(
    () => resolveAccessibleForegroundForSurface(buttonBackground, buttonColor) ?? buttonColor,
    [buttonBackground, buttonColor]
  );

  const getMeetingHostElement = () =>
    document.querySelector('#root > div > rtk-meeting') ?? document.querySelector('rtk-meeting');

  useEffect(() => {
    if (!meeting) return;

    const applyHoverTokenFix = () => {
      const host = getMeetingHostElement();
      const themeDetails = getMeetingThemeDetails(response?.response);
      applyScopedControlbarHoverBackground(host, themeDetails);
      applyRtkBrandTokensForFocusRing(host, themeDetails);
      applyStageAndSidebarLeafPatches(host, themeDetails);
      applyChatSelectorUiTokens(host, themeDetails);
      applyDialogChromeContrast(host, themeDetails);
      applyPrimaryMenuListTextContrast(host, themeDetails);
      injectRtkDebuggerLayoutOverrides(host);
    };
    applyHoverTokenFix();
    const hoverFixTimeouts = [200, 500, 1000, 2000, 4000].map((ms) =>
      window.setTimeout(applyHoverTokenFix, ms)
    );
    meeting.self.on('roomJoined', applyHoverTokenFix);

    const patchScopedPanelTheme = () => {
      const meetingHost = getMeetingHostElement();
      const themeDetails = getMeetingThemeDetails(response?.response);
      applyMeetingScopedPanelTheme(meetingHost, themeDetails);
    };

    patchScopedPanelTheme();
    const timeouts = [200, 500, 1000, 2000, 4000].map((ms) =>
      window.setTimeout(patchScopedPanelTheme, ms)
    );
    meeting.self.on('roomJoined', patchScopedPanelTheme);

    /** Re-patch when nested UI (inner rtk-meeting → rtk-sidebar) mounts after idle/setup. */
    let active = true;
    let shadowObserver = null;
    const attachShadowObserver = () => {
      if (!active) return;
      const host = getMeetingHostElement();
      const sr = host?.shadowRoot;
      if (!sr || shadowObserver) return;
      shadowObserver = new MutationObserver(() => { if (active) patchScopedPanelTheme(); });
      shadowObserver.observe(sr, { childList: true, subtree: true });
    };
    attachShadowObserver();
    const shadowFrameId = requestAnimationFrame(() => {
      if (!active) return;
      attachShadowObserver();
      patchScopedPanelTheme();
    });

    /**
     * rtk-menu-list / rtk-menu-item for chat message context menus mount lazily on click.
     * Shadow encapsulation means the outer MutationObserver never fires for them.
     * Re-run the menu contrast patch one frame after each click so any newly-mounted menus
     * are caught regardless of which theme or how deep the shadow nesting is.
     */
    let menuPatchRafId = null;
    const onMenuClick = () => {
      if (menuPatchRafId) return;
      menuPatchRafId = requestAnimationFrame(() => {
        menuPatchRafId = null;
        if (!active) return;
        const host = getMeetingHostElement();
        const themeDetails = getMeetingThemeDetails(response?.response);
        applyPrimaryMenuListTextContrast(host, themeDetails);
      });
    };
    document.addEventListener('click', onMenuClick, { capture: true });

    return () => {
      active = false;
      cancelAnimationFrame(shadowFrameId);
      hoverFixTimeouts.forEach(clearTimeout);
      meeting.self.off('roomJoined', applyHoverTokenFix);
      timeouts.forEach(clearTimeout);
      shadowObserver?.disconnect();
      meeting.self.off('roomJoined', patchScopedPanelTheme);
      document.removeEventListener('click', onMenuClick, { capture: true });
      if (menuPatchRafId) cancelAnimationFrame(menuPatchRafId);
    };
  }, [meeting, response]);

  useEffect(() => {
    const themeDetails = getMeetingThemeDetails(response?.response);
    const interval = setInterval(() => {
      const controlBarHeader = document.querySelector("#root > div > rtk-meeting")?.shadowRoot?.querySelector("rtk-header");
      const controlBar = document.querySelector("#root > div > rtk-meeting")?.shadowRoot?.querySelector("rtk-controlbar");
      if (controlBar) {
        controlBar.style.backgroundColor = 'transparent';
      }

      if (controlBarHeader) {
        controlBarHeader.style.backgroundColor = 'transparent';
      }

      applyScopedControlbarHoverBackground(getMeetingHostElement(), themeDetails);
      applyMeetingScopedPanelTheme(getMeetingHostElement(), themeDetails);

    }, 1000); 

    return () => clearInterval(interval); 
  }, [meeting, response]);

  const endMeetingForAll = () => {
    const rtkMeetingElement = document.querySelector("#root > div > rtk-meeting");
    const endMeetingButton = rtkMeetingElement?.shadowRoot
      .querySelector("rtk-dialog-manager")?.shadowRoot
      .querySelector("rtk-dialog > rtk-leave-meeting")?.shadowRoot
      .querySelector("div > div.content > rtk-button:nth-child(3)")?.shadowRoot
      .querySelector("button > span.content > slot")
      ?.assignedNodes()
      .find(node => node.textContent.trim() === "End meeting for all");

    if (endMeetingButton) {
      endMeetingButton.parentElement.style.display = 'none'; // Hide the button
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      endMeetingForAll();
    }, 100); // Check every second

    return () => clearInterval(interval); // Clean up the interval on component unmount
  }, [meeting]);

  const changeTitleFontSize = () => {
    const titleElement = document.querySelector("#root > div > rtk-meeting")?.shadowRoot
      .querySelector("#header-center > rtk-meeting-title")?.shadowRoot
      .querySelector("rtk-tooltip");
    if (titleElement) {
        titleElement.style.fontSize = '22px'; // Change this value to the desired font size
    }
  };

  const changeLogoSize = () => {
    const logoImage = document.querySelector("#root > div > rtk-meeting")?.shadowRoot
      .querySelector("#header-left > rtk-logo")?.shadowRoot
      .querySelector("img");

    if (logoImage) {
      logoImage.style.width = '160px';
      logoImage.style.height = '90px';
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      changeLogoSize();
      changeTitleFontSize();
    }, 1000); // Check every second

    return () => clearInterval(interval); // Clean up the interval on component unmount
  }, [meeting]);

  const changeButtonColors = useCallback(() => {
    const rtkMeetingElement = document.querySelector("#root > div > rtk-meeting");
    const setupControlsMediaElement = document.querySelector("#root > div > rtk-meeting").shadowRoot
      .querySelector("rtk-setup-screen")?.shadowRoot
      .querySelector("#setupcontrols-media")
   
      const micToggleElement = setupControlsMediaElement?.querySelector("rtk-mic-toggle")?.shadowRoot.querySelector("rtk-controlbar-button");
      const micToggleTitle = setupControlsMediaElement?.querySelector("rtk-mic-toggle")?.getAttribute("title");
      const cameraToggleTitle = setupControlsMediaElement?.querySelector("rtk-camera-toggle")?.getAttribute("title");
      const isCameraOn = cameraToggleTitle === "Video On";
      const isMicOn = micToggleTitle === "Mic On";
    if (micToggleElement) {
      micToggleElement.style.borderRadius = '8px';
      micToggleElement.style.backgroundColor = `${buttonBackground}`;
    }
    const cameraToggleElement = setupControlsMediaElement?.querySelector("rtk-camera-toggle")?.shadowRoot.querySelector("rtk-controlbar-button");
    if (cameraToggleElement) {
      micToggleElement.style.borderRadius = '8px';
      cameraToggleElement.style.backgroundColor = `${buttonBackground}`;
    }

    const svgElement = setupControlsMediaElement?.querySelector("rtk-mic-toggle")?.shadowRoot.querySelector("rtk-tooltip > rtk-controlbar-button")?.shadowRoot.querySelector("#icon")?.shadowRoot.querySelector("div > svg")
    const svgElementMic = setupControlsMediaElement?.querySelector("rtk-camera-toggle")?.shadowRoot.querySelector("rtk-tooltip > rtk-controlbar-button")?.shadowRoot.querySelector("#icon")?.shadowRoot.querySelector("div > svg");
    
    if (isMicOn && svgElement) {
      svgElement.style.color = `${controlBarIconColor}`;
    }

    if (isCameraOn && svgElementMic) {
      svgElementMic.style.color = `${controlBarIconColor}`;
    }
    if (!rtkMeetingElement) return;
  
    const buttonSelectors = [
      "#controlbar-center > rtk-mic-toggle",
      "#controlbar-center > rtk-camera-toggle",
      "#controlbar-center > rtk-stage-toggle",
      "#controlbar-center > rtk-more-toggle",
      "#controlbar-center > rtk-leave-button",
      "#controlbar-left > rtk-fullscreen-toggle",
      "#controlbar-left > rtk-settings-toggle",
      "#controlbar-left > rtk-screen-share-toggle",
      "#controlbar-right > rtk-chat-toggle",
      "#controlbar-right > rtk-participants-toggle",
      "rtk-setup-screen #setupcontrols-media > rtk-mic-toggle"
    ];

    buttonSelectors.forEach(selector => {
      const buttonElement = rtkMeetingElement.shadowRoot.querySelector(selector)?.shadowRoot.querySelector("rtk-controlbar-button");
      if (buttonElement) {
        buttonElement.style.backgroundColor = `${buttonBackground}`;
      }
    });

    // const customButton = document.createElement('button');
    // customButton.setAttribute('aria-label', 'Copy Link Button');
    // customButton.setAttribute('part', 'button');
  
    // const icon = document.createElement('img');
    // icon.src = 'https://img.icons8.com/ios-filled/50/000000/copy-link.png'; // Copy Link icon URL
    // icon.alt = 'Copy Link Icon';
    // icon.style.width = '24px'; // Adjust the size of the icon
    // icon.style.height = '24px';
    // icon.style.marginBottom = '4px'; // Add some space between the icon and the label
  
    // const label = document.createElement('span');
    // label.classList.add('label');
    // label.setAttribute('part', 'label');
    // label.innerText = 'Copy Link';
    // label.style.color = 'rgb(255, 255, 255)'; // Adjust the color if needed
  
    // customButton.style.position = 'absolute';
    // customButton.style.right = '45px';
    // customButton.style.display = 'flex';
    // customButton.style.flexDirection = 'column';
    // customButton.style.alignItems = 'center';
    // customButton.style.justifyContent = 'center';
    // customButton.style.height = '64px'; 
    // customButton.style.width = '64px'; // Set the width
    // customButton.style.backgroundColor = '#f77979'; // Change background color
    // customButton.style.color = 'white';
    // customButton.style.border = 'none';
    // customButton.style.borderRadius = '8px'; // Adjust border radius
    // customButton.style.cursor = 'pointer';
    // customButton.style.marginLeft = '8px'; // Add some space between buttons
  
    // // Event listener for the button click to copy the URL to clipboard
    // customButton.onclick = () => {
    //   navigator.clipboard.writeText(window.location.href).then(() => {
    //     alert('URL copied to clipboard!');
    //   }).catch(err => {
    //     console.error('Failed to copy: ', err);
    //   });
    // };
  
    // // Append the icon and label to the button
    // customButton.appendChild(icon);
    // customButton.appendChild(label);
  
  
    // // Media query styles for responsiveness
    // const styleSheet = document.createElement("style");
    // styleSheet.type = "text/css";
    // styleSheet.innerText = `
    //   @media (min-width: 1366px) {
    //     button[part="button"] {
    //       right: -45px;
    //       padding: 6px 24px;
    //     }
    //   }
    //   @media (max-width: 768px) {
    //     button[part="button"] {
    //       right: 20px;
    //       padding: 10px 20px;
    //       font-size: 14px;
    //     }
    //   }
    //   @media (max-width: 480px) {
    //     button[part="button"] {
    //       right: 10px;
    //       padding: 8px 16px;
    //       font-size: 12px;
    //     }
    //   }
    // `;
    // // Event listener for the button click
    // customButton.onclick = () => {
    //   navigator.clipboard.writeText(window.location.href).then(() => {
    //     console.error('copy link');
    //   }).catch(err => {
    //     console.error('Failed to copy: ', err);
    //   });
    // };

    // const controlbarCenter = rtkMeetingElement?.shadowRoot?.querySelector("#controlbar-center");
    // if (controlbarCenter) {
    //   controlbarCenter?.appendChild(customButton);
    // }
  }, [buttonBackground, controlBarIconColor]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      // Check if all buttons have been changed
      changeButtonColors();
      const rtkMeetingElement = document.querySelector("#root > div > rtk-meeting");
      const buttonSelectors = [
        "#controlbar-center > rtk-mic-toggle",
        "#controlbar-center > rtk-camera-toggle",
        "#controlbar-center > rtk-stage-toggle",
        "#controlbar-center > rtk-more-toggle",
        "#controlbar-center > rtk-leave-button",
        "#controlbar-left > rtk-fullscreen-toggle",
        "#controlbar-left > rtk-settings-toggle",
        "#controlbar-left > rtk-screen-share-toggle",
        "#controlbar-right > rtk-chat-toggle",
        "#controlbar-right > rtk-participants-toggle",
        "rtk-setup-screen #setupcontrols-media > rtk-mic-toggle"
      ];

      buttonSelectors.forEach(selector => {
        const buttonElement = rtkMeetingElement.shadowRoot.querySelector(selector)?.shadowRoot.querySelector("rtk-controlbar-button");
        const buttonSvgElement = buttonElement?.shadowRoot.querySelector("#icon")?.shadowRoot.querySelector("div > svg");
        const buttonSpanElement = buttonElement?.shadowRoot.querySelector("button > span");
        if (buttonSpanElement) {
            buttonSpanElement.style.color = `${controlBarIconColor}`;
        }

          // Check for mic and camera toggles
      const micToggle = rtkMeetingElement.shadowRoot.querySelector("#controlbar-center > rtk-mic-toggle");
      const cameraToggle = rtkMeetingElement.shadowRoot.querySelector("#controlbar-center > rtk-camera-toggle");
    

        const micTitle = micToggle?.getAttribute("title");
        const cameraTitle = cameraToggle?.getAttribute("title");
        
        const micIsOn = micTitle === "Mic On";
        const cameraIsOn = cameraTitle === "Video On";
        if (buttonSvgElement) {
          if ((selector.includes("rtk-mic-toggle") && micIsOn) || (selector.includes("rtk-camera-toggle") && cameraIsOn)) {
            buttonSvgElement.style.color = `${controlBarIconColor}`;
          }else if ((!selector.includes("rtk-mic-toggle")) && (!selector.includes("rtk-camera-toggle"))) {
            buttonSvgElement.style.color = `${controlBarIconColor}`;
          }
        }
    
  });
  
      const allButtonsChanged = buttonSelectors.every(selector => {
        const buttonElement = rtkMeetingElement.shadowRoot.querySelector(selector)?.shadowRoot.querySelector("rtk-controlbar-button");
        return buttonElement && buttonElement.style.backgroundColor === `${buttonBackground}`;
      });
  
      if (allButtonsChanged) {
        clearInterval(interval);
      }
    }, 100); // Check every second
  
    return () => clearInterval(interval); // Clean up the interval on component unmount
  }, [meeting, changeButtonColors, buttonBackground, controlBarIconColor]);

  return (
    <>
      <RtkMeeting style={{ backgroundColor: 'transparent' }} meeting={meeting} mode='fill' />
      {/* { ( 
        <button onClick={captureScreenshot} className="screenshot-btn">Capture Screenshot</button>
      )} */}
      <style>{`
        .screenshot-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          padding: 10px 20px;
          background-color: #007bff;
          color: #fff;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          z-index: 10;
        }
        .screenshot-btn:hover {
          background-color: #0056b3;
        }
      `}</style>
    </>
  );
}

export default App;
